import React, { useState, useEffect, useRef } from 'react';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import { HeatmapLayer } from 'deck.gl';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { GOOGLE_MAPS_API_KEY } from './config';
import { API_URL } from "./config";
import { Settings, RefreshCw, MapPin, Users, Car, Filter, Calendar, Clock, TrendingUp, Star } from 'lucide-react';
import SideBar from './DashboardLayout/SideBar';

// Google retired google.maps.visualization.HeatmapLayer (removed as of Maps JS API v3.65 —
// see https://developers.google.com/maps/deprecations). deck.gl's HeatmapLayer, rendered through
// a GoogleMapsOverlay on top of the same google.maps.Map instance, is Google's own suggested
// replacement and reproduces the same smooth gradient look.
const HEATMAP_COLOR_RANGE = [
  [0, 0, 255],
  [0, 255, 255],
  [0, 255, 0],
  [255, 255, 0],
  [255, 0, 0]
];

const mapContainerStyle = {
  width: '100%',
  height: '100vh',
};

const HeatMapRideDemandMap = () => {
  const [heatmapData, setHeatmapData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mainSidebarCollapsed, setMainSidebarCollapsed] = useState(false);
  const [heatmapSettings, setHeatmapSettings] = useState({
    radius: 80,
    opacity: 0.8,
    showPrivateRides: true,
    showRideshareTrips: true,
    showPickups: true,
    showDestinations: true,
    showFrequencyMode: false,
    clusterRadius: 0.001, // ~100m clustering radius
    dataLimit: 1000 // Limit data points for performance
  });
  const [stats, setStats] = useState({
    privateTrips: 0,
    rideshareTrips: 0,
    totalPoints: 0,
    lastUpdated: null,
    frequentOrigins: [],
    frequentDestinations: [],
    originFrequency: {},
    destinationFrequency: {}
  });
  const mapRef = useRef(null);
  const overlayRef = useRef(null);
  const markersRef = useRef([]);

  useEffect(() => {
    const fetchTripData = async () => {
      try {
        setLoading(true);
        
        // Fetch private rides from trips table
        const privateTripsResponse = await fetch(`${API_URL}/trip?limit=${heatmapSettings.dataLimit}`);
        const privateTrips = await privateTripsResponse.json();
        console.log('Private trips data:', privateTrips.length, 'trips');
        
        // Fetch rideshare trips from rideshare_trips table
        const rideshareTripsResponse = await fetch(`${API_URL}/rideshare/trips?limit=${heatmapSettings.dataLimit}`);
        const rideshareTrips = await rideshareTripsResponse.json();
        console.log('Rideshare trips data:', rideshareTrips.length, 'trips');
        
        // Process private rides data - include both pickup and destination
        const privateHeatmapData = [];
        const originFrequency = {};
        const destinationFrequency = {};
        let privatePickupCount = 0;
        let privateDestinationCount = 0;
        
        privateTrips.forEach(trip => {
          // Add pickup location (real trip-table columns: origin_location_lat/origin_location_long —
          // not pickup_lat/pickup_lng, which don't exist on this table)
          if (trip.origin_location_lat && trip.origin_location_long && heatmapSettings.showPickups && heatmapSettings.showPrivateRides) {
            const lat = parseFloat(trip.origin_location_lat);
            const lng = parseFloat(trip.origin_location_long);
            const key = `${lat.toFixed(4)},${lng.toFixed(4)}`; // Cluster nearby locations
            
            privateHeatmapData.push({
              lat, lng,
              weight: 1 // Base weight for private rides pickup
            });
            privatePickupCount++;
            
            // Track frequency
            originFrequency[key] = (originFrequency[key] || 0) + 1;
          }
          
          // Add destination location (real trip-table columns: destination_lat/destination_long)
          if (trip.destination_lat && trip.destination_long && heatmapSettings.showDestinations && heatmapSettings.showPrivateRides) {
            const lat = parseFloat(trip.destination_lat);
            const lng = parseFloat(trip.destination_long);
            const key = `${lat.toFixed(4)},${lng.toFixed(4)}`; // Cluster nearby locations
            
            privateHeatmapData.push({
              lat, lng,
              weight: 0.8 // Slightly lower weight for destinations
            });
            privateDestinationCount++;
            
            // Track frequency
            destinationFrequency[key] = (destinationFrequency[key] || 0) + 1;
          }
        });

        // Process rideshare trips data - include both origin and destination
        const rideshareHeatmapData = [];
        let ridesharePickupCount = 0;
        let rideshareDestinationCount = 0;
        
        rideshareTrips.forEach(trip => {
          // Add origin location
          if (trip.origin_lat && trip.origin_lng && heatmapSettings.showPickups && heatmapSettings.showRideshareTrips) {
            const lat = parseFloat(trip.origin_lat);
            const lng = parseFloat(trip.origin_lng);
            const key = `${lat.toFixed(4)},${lng.toFixed(4)}`; // Cluster nearby locations
            
            rideshareHeatmapData.push({
              lat, lng,
              weight: 1.5 // Higher weight for rideshare pickups
            });
            ridesharePickupCount++;
            
            // Track frequency
            originFrequency[key] = (originFrequency[key] || 0) + 1;
          }
          
          // Add destination location
          if (trip.destination_lat && trip.destination_lng && heatmapSettings.showDestinations && heatmapSettings.showRideshareTrips) {
            const lat = parseFloat(trip.destination_lat);
            const lng = parseFloat(trip.destination_lng);
            const key = `${lat.toFixed(4)},${lng.toFixed(4)}`; // Cluster nearby locations
            
            rideshareHeatmapData.push({
              lat, lng,
              weight: 1.2 // Weight for rideshare destinations
            });
            rideshareDestinationCount++;
            
            // Track frequency
            destinationFrequency[key] = (destinationFrequency[key] || 0) + 1;
          }
        });

        // Calculate frequent locations (top 10)
        const frequentOrigins = Object.entries(originFrequency)
          .sort(([,a], [,b]) => b - a)
          .slice(0, 10)
          .map(([key, count]) => {
            const [lat, lng] = key.split(',').map(Number);
            return { lat, lng, count };
          });

        const frequentDestinations = Object.entries(destinationFrequency)
          .sort(([,a], [,b]) => b - a)
          .slice(0, 10)
          .map(([key, count]) => {
            const [lat, lng] = key.split(',').map(Number);
            return { lat, lng, count };
          });

        // Combine both datasets
        const combinedHeatmapData = [...privateHeatmapData, ...rideshareHeatmapData];
        console.log('Combined heatmap data points:', combinedHeatmapData.length);
        console.log('Private heatmap points:', privateHeatmapData.length);
        console.log('Rideshare heatmap points:', rideshareHeatmapData.length);
        
        // Update stats
        setStats({
          privateTrips: privateTrips.length,
          rideshareTrips: rideshareTrips.length,
          totalPoints: combinedHeatmapData.length,
          lastUpdated: new Date(),
          privatePickupCount,
          privateDestinationCount,
          ridesharePickupCount,
          rideshareDestinationCount,
          frequentOrigins,
          frequentDestinations,
          originFrequency,
          destinationFrequency
        });
        
        setHeatmapData(combinedHeatmapData);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching trip data:', error);
        setLoading(false);
      }
    };

    fetchTripData();
    
    // Refresh data every 30 seconds
    const interval = setInterval(fetchTripData, 30000);

    return () => clearInterval(interval);
  }, [heatmapSettings.showPrivateRides, heatmapSettings.showRideshareTrips, heatmapSettings.showPickups, heatmapSettings.showDestinations, heatmapSettings.dataLimit]);

  useEffect(() => {
    if (overlayRef.current) {
      // Rebuild the deck.gl heatmap layer whenever data/settings change — cheap, deck.gl diffs
      // layers by id internally rather than mutating a live layer object like the old API did.
      const layers = heatmapData.length > 0
        ? [
            new HeatmapLayer({
              id: 'ride-demand-heatmap',
              data: heatmapData,
              getPosition: (d) => [d.lng, d.lat],
              getWeight: (d) => d.weight,
              radiusPixels: heatmapSettings.radius,
              opacity: heatmapSettings.opacity,
              colorRange: HEATMAP_COLOR_RANGE,
              threshold: 0.05
            })
          ]
        : [];
      overlayRef.current.setProps({ layers });
    }

    // Add frequency markers if in frequency mode
    if (mapRef.current && heatmapSettings.showFrequencyMode) {
      // Clear existing markers
      markersRef.current.forEach(marker => marker.setMap(null));
      markersRef.current = [];

      // Add frequent origin markers
      stats.frequentOrigins.forEach((origin, index) => {
        const marker = new window.google.maps.Marker({
          position: { lat: origin.lat, lng: origin.lng },
          map: mapRef.current,
          title: `Frequent Pickup: ${origin.count} trips`,
          label: {
            text: `${origin.count}`,
            color: 'white',
            fontWeight: 'bold',
            fontSize: '12px'
          },
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: Math.min(origin.count * 2, 30), // Size based on frequency
            fillColor: '#4CAF50', // Green for origins
            fillOpacity: 0.8,
            strokeColor: '#2E7D32',
            strokeWeight: 2
          }
        });
        markersRef.current.push(marker);
      });

      // Add frequent destination markers
      stats.frequentDestinations.forEach((destination, index) => {
        const marker = new window.google.maps.Marker({
          position: { lat: destination.lat, lng: destination.lng },
          map: mapRef.current,
          title: `Frequent Destination: ${destination.count} trips`,
          label: {
            text: `${destination.count}`,
            color: 'white',
            fontWeight: 'bold',
            fontSize: '12px'
          },
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: Math.min(destination.count * 2, 30), // Size based on frequency
            fillColor: '#FF5722', // Orange for destinations
            fillOpacity: 0.8,
            strokeColor: '#D84315',
            strokeWeight: 2
          }
        });
        markersRef.current.push(marker);
      });
    } else if (!heatmapSettings.showFrequencyMode && markersRef.current.length > 0) {
      // Clear markers if not in frequency mode
      markersRef.current.forEach(marker => marker.setMap(null));
      markersRef.current = [];
    }
  }, [heatmapData, heatmapSettings, stats]);

  return (
    <div className="flex h-screen">
      {/* Main Dashboard Sidebar */}
      <SideBar collapsed={mainSidebarCollapsed} setCollapsed={setMainSidebarCollapsed} />
      
      {/* Heat Map Controls Sidebar */}
      <div className={`${sidebarCollapsed ? 'w-16' : 'w-80'} bg-white shadow-lg transition-all duration-300 flex flex-col`}>
        {/* Sidebar Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            {!sidebarCollapsed && (
              <h2 className="text-lg font-bold text-gray-800">Heat Map Controls</h2>
            )}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-2 rounded hover:bg-gray-100 transition-colors"
            >
              <Settings size={20} className="text-gray-600" />
            </button>
          </div>
        </div>

        {/* Sidebar Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {!sidebarCollapsed ? (
            <>
              {/* Statistics Section */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                  <Users size={16} className="mr-2" />
                  Statistics
                </h3>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Private Trips:</span>
                    <span className="font-medium">{stats.privateTrips}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Rideshare Trips:</span>
                    <span className="font-medium">{stats.rideshareTrips}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Points:</span>
                    <span className="font-medium text-blue-600">{stats.totalPoints}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Data Limit:</span>
                    <span className="font-medium text-purple-600">{heatmapSettings.dataLimit}</span>
                  </div>
                  {stats.lastUpdated && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Last Updated:</span>
                      <span className="font-medium">
                        {stats.lastUpdated.toLocaleTimeString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Filters Section */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                  <Filter size={16} className="mr-2" />
                  Filters
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={heatmapSettings.showPrivateRides}
                        onChange={(e) => setHeatmapSettings(prev => ({
                          ...prev,
                          showPrivateRides: e.target.checked
                        }))}
                        className="rounded text-blue-600"
                      />
                      <span className="text-sm text-gray-700">Show Private Rides</span>
                    </label>
                  </div>
                  <div>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={heatmapSettings.showRideshareTrips}
                        onChange={(e) => setHeatmapSettings(prev => ({
                          ...prev,
                          showRideshareTrips: e.target.checked
                        }))}
                        className="rounded text-blue-600"
                      />
                      <span className="text-sm text-gray-700">Show Rideshare Trips</span>
                    </label>
                  </div>
                  <div>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={heatmapSettings.showPickups}
                        onChange={(e) => setHeatmapSettings(prev => ({
                          ...prev,
                          showPickups: e.target.checked
                        }))}
                        className="rounded text-blue-600"
                      />
                      <span className="text-sm text-gray-700">Show Pickups</span>
                    </label>
                  </div>
                  <div>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={heatmapSettings.showDestinations}
                        onChange={(e) => setHeatmapSettings(prev => ({
                          ...prev,
                          showDestinations: e.target.checked
                        }))}
                        className="rounded text-blue-600"
                      />
                      <span className="text-sm text-gray-700">Show Destinations</span>
                    </label>
                  </div>
                  <div>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={heatmapSettings.showFrequencyMode}
                        onChange={(e) => setHeatmapSettings(prev => ({
                          ...prev,
                          showFrequencyMode: e.target.checked
                        }))}
                        className="rounded text-purple-600"
                      />
                      <span className="text-sm text-gray-700 font-medium">Show Frequent Locations</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Frequent Locations Section */}
              {heatmapSettings.showFrequencyMode && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                    <TrendingUp size={16} className="mr-2" />
                    Top Frequent Locations
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <h4 className="text-xs font-medium text-green-700 mb-2 flex items-center">
                        <Star size={12} className="mr-1" />
                        Top Pickups
                      </h4>
                      <div className="space-y-1">
                        {stats.frequentOrigins.slice(0, 5).map((origin, index) => (
                          <div key={index} className="flex justify-between text-xs">
                            <span className="text-gray-600">#{index + 1}</span>
                            <span className="font-medium text-green-600">{origin.count} trips</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h4 className="text-xs font-medium text-orange-700 mb-2 flex items-center">
                        <Star size={12} className="mr-1" />
                        Top Destinations
                      </h4>
                      <div className="space-y-1">
                        {stats.frequentDestinations.slice(0, 5).map((destination, index) => (
                          <div key={index} className="flex justify-between text-xs">
                            <span className="text-gray-600">#{index + 1}</span>
                            <span className="font-medium text-orange-600">{destination.count} trips</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Heatmap Settings */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                  <Settings size={16} className="mr-2" />
                  Heatmap Settings
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">
                      Radius: {heatmapSettings.radius}
                    </label>
                    <input
                      type="range"
                      min="20"
                      max="150"
                      value={heatmapSettings.radius}
                      onChange={(e) => setHeatmapSettings(prev => ({
                        ...prev,
                        radius: parseInt(e.target.value)
                      }))}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">
                      Opacity: {heatmapSettings.opacity.toFixed(1)}
                    </label>
                    <input
                      type="range"
                      min="0.1"
                      max="1.0"
                      step="0.1"
                      value={heatmapSettings.opacity}
                      onChange={(e) => setHeatmapSettings(prev => ({
                        ...prev,
                        opacity: parseFloat(e.target.value)
                      }))}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">
                      Data Limit: {heatmapSettings.dataLimit} trips
                    </label>
                    <select
                      value={heatmapSettings.dataLimit}
                      onChange={(e) => setHeatmapSettings(prev => ({
                        ...prev,
                        dataLimit: parseInt(e.target.value)
                      }))}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                    >
                      <option value="500">500 trips (faster)</option>
                      <option value="1000">1,000 trips (balanced)</option>
                      <option value="2000">2,000 trips (detailed)</option>
                      <option value="5000">5,000 trips (comprehensive)</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
      Higher limits provide more data but may be slower
    </p>
                  </div>
                </div>
              </div>

              {/* Legend */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                  <MapPin size={16} className="mr-2" />
                  Legend
                </h3>
                <div className="space-y-2">
                  <div className="text-sm text-gray-600">
                    <p>🔵 Low Demand</p>
                    <p>🟡 Medium Demand</p>
                    <p>🔴 High Demand</p>
                  </div>
                  {heatmapSettings.showFrequencyMode && (
                    <div className="text-xs text-gray-500 space-y-1 mt-2 pt-2 border-t border-gray-200">
                      <p className="font-medium">Frequency Markers:</p>
                      <p>🟢 Green Circles = Frequent Pickups</p>
                      <p>🟠 Orange Circles = Frequent Destinations</p>
                      <p>📏 Circle Size = Trip Frequency</p>
                    </div>
                  )}
                  <div className="text-xs text-gray-500 space-y-1">
                    <p>Private Pickups: Weight 1.0</p>
                    <p>Private Destinations: Weight 0.8</p>
                    <p>Rideshare Pickups: Weight 1.5</p>
                    <p>Rideshare Destinations: Weight 1.2</p>
                  </div>
                </div>
              </div>

              {/* Refresh Button */}
              <button
                onClick={() => window.location.reload()}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 transition-colors flex items-center justify-center"
              >
                <RefreshCw size={16} className="mr-2" />
                Refresh Data
              </button>
            </>
          ) : (
            // Collapsed state - just icons
            <div className="flex flex-col items-center space-y-4">
              <div className="p-2 rounded hover:bg-gray-100 cursor-pointer" title="Statistics">
                <Users size={20} className="text-gray-600" />
              </div>
              <div className="p-2 rounded hover:bg-gray-100 cursor-pointer" title="Filters">
                <Filter size={20} className="text-gray-600" />
              </div>
              <div className="p-2 rounded hover:bg-gray-100 cursor-pointer" title="Settings">
                <Settings size={20} className="text-gray-600" />
              </div>
              <div className="p-2 rounded hover:bg-gray-100 cursor-pointer" title="Refresh">
                <RefreshCw size={20} className="text-gray-600" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Map Container */}
      <div className="flex-1 relative">
        {/* Top Legend Overlay */}
        <div className="absolute top-4 right-4 z-10 bg-white rounded-lg shadow-lg p-4">
          <h2 className="text-lg font-bold text-gray-800 mb-2">Ride Demand Heat Map</h2>
          <div className="text-sm text-gray-600">
            <p>🔵 Low Demand</p>
            <p>🟡 Medium Demand</p>
            <p>🔴 High Demand</p>
          </div>
          {heatmapSettings.showFrequencyMode && (
            <div className="mt-2 text-xs text-gray-500 pt-2 border-t border-gray-200">
              <p className="font-medium">📍 Frequent Locations</p>
              <p>🟢 Pickups: {stats.frequentOrigins.length} spots</p>
              <p>🟠 Destinations: {stats.frequentDestinations.length} spots</p>
            </div>
          )}
          <div className="mt-2 text-xs text-gray-500">
            <p>Total Points: {heatmapData.length}</p>
          </div>
          {loading && (
            <div className="mt-2 text-xs text-blue-600">
              Updating data...
            </div>
          )}
        </div>
        
        <LoadScript
          googleMapsApiKey={GOOGLE_MAPS_API_KEY}
        >
          <GoogleMap
            mapContainerStyle={{
              width: '100%',
              height: '100vh'
            }}
            center={{ lat: -17.8292, lng: 31.0522 }} // Harare, Zimbabwe
            zoom={12}
            onLoad={(map) => {
              mapRef.current = map;
              overlayRef.current = new GoogleMapsOverlay({ layers: [] });
              overlayRef.current.setMap(map);
            }}
            mapTypeId="roadmap"
          >
            {/* Heatmap is rendered by the deck.gl GoogleMapsOverlay above, not as a child here */}
          </GoogleMap>
        </LoadScript>
      </div>
    </div>
  );
};

export default HeatMapRideDemandMap;
